<script lang="ts">
import DataQualityAccountRow from '@/views/admin/DataQualityAccountRow.vue';
import DataQualityPolicyRow from '@/views/admin/DataQualityPolicyRow.vue';
import DataQualityRollupEntityRow from '@/views/admin/DataQualityRollupEntityRow.vue';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import EditPolicyModal from '@/components/EditPolicyModal.vue';
import { VSkeletonLoader } from 'vuetify/components/VSkeletonLoader';
import Toggle from '@/components/v2/Toggle.vue';
import DataQualityProgramRow from '@/views/admin/DataQualityProgramRow.vue';
import FilterBar from '@/components/FilterBar.vue';
import FilterButton from '@/components/FilterButton.vue';
import PagingControl from '@/components/v2/PagingControl.vue';

export default {
  name: 'DataQuality',
  components: {
    DataQualityProgramRow,
    Toggle,
    EmptyDisplay,
    DataQualityAccountRow,
    DataQualityPolicyRow,
    DataQualityRollupEntityRow,
    FilterBar,
    FilterButton,
    ManagedDateRange,
    EditPolicyModal,
    VSkeletonLoader,
    PagingControl,
  },
};
</script>

<script setup lang="ts">
import filingSrc from '@/assets/images/filing.svg';
import {
  DataQualityAccount,
  DataQualityAccountStat,
  DataQualityPolicy,
  DataQualityPolicyStat,
  DataQualityProgram,
  DataQualityProgramStats,
  DataQualityRollupEntity,
  DataQualityRollupEntityStat,
  DataQualityRouteQuery,
  DataQualityRule,
  EntityTypeStat,
  RollupEntityRules,
  RuleFilter,
} from '@/types/DataQuality';
import { dataQualityService } from '@/services/DataQualityService';
import { multiSeriesColors, paletteColors } from '@/utilities/Color';
import { percentage } from '@/utilities/Number';
import { DateTime } from 'luxon';
import { DatePayload } from '@/types/DatePayload';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const ACCOUNT_VIOLATIONS = 'Account Violations';
const POLICY_VIOLATIONS = 'Policy Violations';
const ROLLUP_ENTITIES_VIOLATIONS = 'Rollup Entities Violations';
const PROGRAM_VIOLATIONS = 'Program Violations';
const PAGE_SIZE = 50;

type LoadFunction = { (): Promise<void> };

const route = useRoute();
const router = useRouter();

const dataQualityDataSource = (caption: string, totalItems: number, totalViolations: number) => {
  const inViolationPercent = percentage((totalViolations / totalItems) * 100);
  const noViolations = totalItems - totalViolations;
  const noViolationsPercent = percentage((noViolations / totalItems) * 100);
  const data = [
    {
      label: 'In Violation',
      displayValue: `In Violation, ${inViolationPercent} (${totalViolations})`,
      value: totalViolations,
    },
    {
      label: 'No Violation',
      displayValue: `No Violation, ${noViolationsPercent} (${noViolations})`,
      value: noViolations,
    },
  ];
  return {
    chart: {
      caption,
      captionAlignment: 'center',
      animation: false,
      showPercentValues: '1',
      showPercentInTooltip: '0',
      startingAngle: 310,
      exportFilename: caption,
      theme: 'cacs',
      showLegend: true,
      decimals: 0,
      paletteColors: multiSeriesColors,
    },
    data,
  };
};

const violationsByRuleDataSource = (
  caption: string,
  rules: number,
  totalViolations: number,
  countSupplier: { (rule: number): number },
) => {
  const data = Array.from(Array(rules).keys()).reduce(
    (acc, i) => {
      const n = i + 1;
      const count = countSupplier(n);
      const percent = percentage((count / totalViolations) * 100);
      acc.push({ label: `Rule ${n}`, displayValue: `Rule ${n}, ${percent} (${count})`, value: count });
      return acc;
    },
    [] as { label: string; displayValue: string; value: number }[],
  );
  return {
    chart: {
      caption,
      captionAlignment: 'center',
      animation: false,
      showPercentValues: '1',
      showPercentInTooltip: '0',
      startingAngle: 310,
      theme: 'cacs',
      exportFilename: caption,
      showLegend: true,
      decimals: 0,
      paletteColors: paletteColors,
    },
    data,
  };
};

const rollupEntityViolationsByRuleDataSource = (caption: string, entityTypeStats: EntityTypeStat[]): unknown => {
  const totalViolationsAcrossAll = entityTypeStats.reduce((acc, curr) => acc + curr.totalEntitiesWithViolations, 0);
  const data: { label: string; displayValue: string; value: number }[] = [];

  entityTypeStats.forEach((stat) => {
    const entityType = stat.entityType;

    // For Rule 1
    if (stat.rule1 > 0) {
      const percentRule1 = percentage((stat.rule1 / totalViolationsAcrossAll) * 100);
      data.push({
        label: `${entityType}`,
        displayValue: `${entityType} (Rule 1), ${percentRule1} (${stat.rule1})`,
        value: stat.rule1,
      });
    }

    // For Rule 2 (if applicable)
    if (stat.rule2 && stat.rule2 > 0) {
      const percentRule2 = percentage((stat.rule2 / totalViolationsAcrossAll) * 100);
      data.push({
        label: `${entityType}`,
        displayValue: `${entityType} (Rule 2), ${percentRule2} (${stat.rule2})`,
        value: stat.rule2,
      });
    }
  });

  return {
    chart: {
      caption,
      captionAlignment: 'center',
      animation: false,
      showPercentValues: '1',
      showPercentInTooltip: '0',
      startingAngle: 310,
      theme: 'cacs',
      exportFilename: caption,
      showLegend: true,
      decimals: 0,
      paletteColors: paletteColors,
    },
    data,
  };
};

const accountRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Prospect accounts with internal policies.',
    value: 1,
    resolution: 'Either change Account to be "Insured" in Epic or tag Policies as "AOR" (External).',
  },
  {
    key: 'r2',
    title: 'Rule 2',
    desc: 'Accounts w/o defined ownership type.',
    value: 2,
    resolution: 'Assign either "Public" or "Private" ownership.',
  },
  {
    key: 'r3',
    title: 'Rule 3',
    desc: 'Public accounts w/o either a ticker or stock exchange.',
    value: 3,
    resolution: 'Assign ticker and/or symbol as appropriate.',
  },
  { key: 'r4', title: 'Rule 4', desc: 'Accounts w/o a domain name.', value: 4, resolution: 'Update domain name.' },
  { key: 'r5', title: 'Rule 5', desc: 'Accounts w/o a SIC code.', value: 5, resolution: 'Update SIC code.' },
  { key: 'r6', title: 'Rule 6', desc: 'Accounts w/o Industry.', value: 6, resolution: 'Update Industry.' },
  {
    key: 'r7',
    title: 'Rule 7',
    desc: 'Stale prospect accounts.',
    value: 7,
    resolution: 'Quarterly review of accounts to determine if they should be marked inactive.',
  },
] as DataQualityRule[];

const policyRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Policies without Aggregate Limit or Occurrence Limit (Fees Bonds, and Work Comp excluded)',
    value: 1,
    resolution: 'Update Aggregate Limit/Occurrence Limit',
  },
  {
    key: 'r2',
    title: 'Rule 2',
    desc: 'Primary Policies without a Retention or Excess policies with a retention (Fees and Bonds Excluded)',
    value: 2,
    resolution: 'Update Retention or P_E_Flag',
  },
  {
    key: 'r3',
    title: 'Rule 3',
    desc: 'Primary Policies with an Attachment Point or Excess Policies without a Attachment Point (Fees and Bonds Excluded)',
    value: 3,
    resolution: 'Update Attachment Point or P_E_Flag',
  },
  {
    key: 'r4',
    title: 'Rule 4',
    desc: 'Policies where the Aggregate or Occurrence Limit is greater than the Quota Share total (Fees and Bonds Excluded)',
    value: 4,
    resolution: 'Update Update Aggregate Limit/Occurrence Limit or Quota Share Total',
  },
  {
    key: 'r5',
    title: 'Rule 5',
    desc: 'Policies with a non-zero Limit or Attachment Point less than or equal to $1,000 (Fees and Bonds Excluded)',
    value: 5,
    resolution: 'Update Limit/Attachment point',
  },
  {
    key: 'r6',
    title: 'Rule 6',
    desc: 'Policies with a non-zero Limit greater than or equal to $100M (Fees and Bonds Excluded)',
    value: 6,
    resolution: 'Update Limit',
  },
  {
    key: 'r7',
    title: 'Rule 7',
    desc: 'Internally placed Policies without Premium or Fees',
    value: 7,
    resolution: 'Update Premium/Fees',
  },
  {
    key: 'r8',
    title: 'Rule 8',
    desc: 'Invalid Effective/Expiration date',
    value: 8,
    resolution: 'Update Effective/Expiration Date',
  },
  {
    key: 'r9',
    title: 'Rule 9',
    desc: 'Bad Policy number (TBD, DO NOT USE, blank, long character count is greater than 25)',
    value: 9,
    resolution: 'Update Policy Number',
  },
  {
    key: 'r10',
    title: 'Rule 10',
    desc: 'Policies without an uploaded binder',
    value: 10,
    resolution: 'Attach a binder to the Policy',
  },
  {
    key: 'r11',
    title: 'Rule 11',
    desc: 'Policies without an uploaded policy that are 30 days past the policy effective date',
    value: 11,
    resolution: 'Upload a Policy',
  },
] as DataQualityRule[];

const rollupEntityPolicyTypeRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Policy type w/o defined coverage type.',
    value: 1,
    resolution: 'Assign Coverage Type',
    type: 'Policy Type',
  },
] as DataQualityRule[];

const rollupEntityIssuingCompanyRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Issuing Company w/o defined Insurer Group/Carrier.',
    value: 1,
    resolution: 'Assign Insurer Group/Carrier',
    type: 'Issuing Company',
  },
  {
    key: 'r2',
    title: 'Rule 2',
    desc: 'No AM Best rating assigned for this issuing company.',
    value: 2,
    resolution: 'Assign AM Best Rating',
    type: 'Issuing Company',
  },
] as DataQualityRule[];

const rollupEntityWholesalerRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Wholesaler w/o defined wholesaler group.',
    value: 1,
    resolution: 'Assign Wholesaler Group',
    type: 'Wholesaler',
  },
] as DataQualityRule[];

const programRules = [
  {
    key: 'r1',
    title: 'Rule 1',
    desc: 'Programs without exposure information',
    value: 1,
    resolution: 'Enter the appropriate exposure metrics with an effective date before the policy’s effective date',
  },
  {
    key: 'r2',
    title: 'Rule 2',
    desc: 'Programs with a maximum limit of $200M or greater',
    value: 2,
    resolution: 'Review programs for data accuracy and compliance',
  },
  {
    key: 'r3',
    title: 'Rule 3',
    desc: 'Programs without servicing roles',
    value: 3,
    resolution: 'Enter servicing role information for the program’s policies in Epic',
  },
  {
    key: 'r4',
    title: 'Rule 4',
    desc: 'Programs without a Tower Visualization',
    value: 4,
    resolution: 'Visit the Tower Builder to create a visualization',
  },
] as DataQualityRule[];

// REFS
// ------------------------------------------------
const showEditPolicyModal = ref(false);
const editPolicyId = ref(0);
const editPolicyNumber = ref('');
const loadingAll = ref(true);
const loadingViolations = ref(false);
const categoryFilterValue = ref(ACCOUNT_VIOLATIONS);
const selectedCategory = ref(ACCOUNT_VIOLATIONS);
const showCurrentOnly = ref(false);
const showFavoritesOnly = ref(false);
const showWithServicingRolesOnly = ref(false);
const accountViolations = ref([] as DataQualityAccount[]);
const accountStats = ref({} as DataQualityAccountStat);
const policyViolations = ref([] as DataQualityPolicy[]);
const policyStats = ref({} as DataQualityPolicyStat);
const rollupEntityViolations = ref([] as DataQualityRollupEntity[]);
const rollupEntityStats = ref({} as DataQualityRollupEntityStat);
const programViolations = ref([] as DataQualityProgram[]);
const programStats = ref({} as DataQualityProgramStats);
const offset = ref(0);
const totalViolations = ref(0);
const filteredViolationCount = ref(0);
const ruleIndexFilterValue = ref([] as RuleFilter[]);
const selectedRuleIndexes = ref([] as RuleFilter[]);
const startDate = ref(DateTime.local().minus({ days: 366 }).toJSDate());
const endDate = ref(DateTime.local().minus({ days: 1 }).toJSDate());

// Computed
// ------------------------------------------------
const noViolations = computed(() => {
  switch (selectedCategory.value) {
    case ACCOUNT_VIOLATIONS:
      return accountViolations.value.length === 0;
    case POLICY_VIOLATIONS:
      return policyViolations.value.length === 0;
    case ROLLUP_ENTITIES_VIOLATIONS:
      return rollupEntityViolations.value.length === 0;
    case PROGRAM_VIOLATIONS:
      return programViolations.value.length === 0;
    default:
      return true;
  }
});

const violationCategoryTitle = computed(() => selectedCategory.value);

const ruleFilterTitle = computed(() => {
  if (selectedRuleIndexes.value.length === 0 || selectedRuleIndexes.value.length > 3) {
    return 'Rules';
  }

  return availableRules.value
    .filter((rule) =>
      selectedRuleIndexes.value.some(
        (selectedRule) =>
          selectedRule.value === rule.value &&
          (selectedCategory.value === ROLLUP_ENTITIES_VIOLATIONS ? selectedRule.type === rule.type : true),
      ),
    )
    .map((rule) =>
      selectedCategory.value === ROLLUP_ENTITIES_VIOLATIONS && rule.type ? `${rule.type} ${rule.title}` : rule.title,
    )
    .join(' OR ');
});

const ruleFilterActive = computed(() => selectedRuleIndexes.value.length > 0);

const availableRules = computed(() => {
  switch (selectedCategory.value) {
    case ACCOUNT_VIOLATIONS:
      return accountRules;
    case POLICY_VIOLATIONS:
      return policyRules;
    case ROLLUP_ENTITIES_VIOLATIONS:
      return [...rollupEntityPolicyTypeRules, ...rollupEntityIssuingCompanyRules, ...rollupEntityWholesalerRules];
    case PROGRAM_VIOLATIONS:
      return programRules;
    default:
      return [];
  }
});

const effectiveRules = computed<RuleFilter[]>(() => {
  if (selectedRuleIndexes.value.length > 0) {
    return selectedRuleIndexes.value;
  }

  switch (selectedCategory.value) {
    case ACCOUNT_VIOLATIONS:
      return accountRules.map((rule) => ({ value: rule.value }));

    case POLICY_VIOLATIONS:
      return policyRules.map((rule) => ({ value: rule.value }));

    case ROLLUP_ENTITIES_VIOLATIONS:
      return [...rollupEntityPolicyTypeRules, ...rollupEntityIssuingCompanyRules, ...rollupEntityWholesalerRules].map(
        (rule) => ({ value: rule.value, type: rule.type }),
      );

    case PROGRAM_VIOLATIONS:
      return programRules.map((rule) => ({ value: rule.value }));

    default:
      return [];
  }
});

const showingAccountViolations = computed(() => selectedCategory.value === ACCOUNT_VIOLATIONS);

const showingPolicyViolations = computed(() => selectedCategory.value === POLICY_VIOLATIONS);

const showingRollupEntitiesViolations = computed(() => selectedCategory.value === ROLLUP_ENTITIES_VIOLATIONS);

const showingProgramViolations = computed(() => selectedCategory.value === PROGRAM_VIOLATIONS);

const categories = computed(() => [
  ACCOUNT_VIOLATIONS,
  POLICY_VIOLATIONS,
  ROLLUP_ENTITIES_VIOLATIONS,
  PROGRAM_VIOLATIONS,
]);

const showToggleFilters = computed(() => {
  switch (selectedCategory.value) {
    case ROLLUP_ENTITIES_VIOLATIONS:
    case PROGRAM_VIOLATIONS:
      return false;
    default:
      return true;
  }
});

const accountDataQuality = computed(() =>
  dataQualityDataSource('Account Data Quality', accountStats.value.totalAccounts, accountStats.value.totalViolations),
);

const accountViolationsByRule = computed(() =>
  violationsByRuleDataSource('Account Violations By Rule', 7, accountStats.value.totalViolations, (n: number) =>
    Reflect.get(accountStats.value, `rule${n}`),
  ),
);

const policyDataQuality = computed(() =>
  dataQualityDataSource('Policy Data Quality', policyStats.value.totalPolicies, policyStats.value.totalViolations),
);

const policyViolationsByRule = computed(() =>
  violationsByRuleDataSource('Policy Violations By Rule', 11, policyStats.value.totalViolations, (n: number) =>
    Reflect.get(policyStats.value, `rule${n}`),
  ),
);

const rollupEntityDataQuality = computed(() =>
  dataQualityDataSource(
    'Rollup Entity Data Quality',
    rollupEntityStats.value.totalEntities,
    rollupEntityStats.value.totalEntitiesWithViolations,
  ),
);

const rollupEntityViolationsByRule = computed(() =>
  rollupEntityViolationsByRuleDataSource(
    'Rollup Entity Violations By Rule',
    rollupEntityStats.value.entityTypeStats ?? [],
  ),
);

const programDataQuality = computed(() =>
  dataQualityDataSource('Program Data Quality', programStats.value.totalPrograms, programStats.value.totalViolations),
);

const programViolationsByRule = computed(() =>
  violationsByRuleDataSource('Program Violations By Rule', 4, programStats.value.totalViolations, (n: number) =>
    Reflect.get(programStats.value, `rule${n}`),
  ),
);

const groupedRules = computed(() =>
  availableRules.value.reduce(
    (
      grouped: {
        [key: string]: DataQualityRule[];
      },
      rule,
    ) => {
      const { type = '' } = rule;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(rule);
      return grouped;
    },
    {},
  ),
);

// computed for pagination
const currPage = computed(() => Math.floor(offset.value / PAGE_SIZE) + 1);

const totalPages = computed(() => {
  return Math.ceil(filteredViolationCount.value / PAGE_SIZE);
});

// WATCHERS
// ------------------------------------------------
watch(selectedCategory, (newVal: string) => {
  categoryFilterValue.value = newVal;
});

watch(selectedRuleIndexes, (newVal: RuleFilter[]) => {
  ruleIndexFilterValue.value = newVal;
});

// LIFECYLE HOOKS
// ------------------------------------------------
onBeforeMount(async () => {
  setSearchCriteriaFromQuery();

  await Promise.all([fetchListData(), fetchStats(), fetchViolationCount()]);
});

// METHODS
// ------------------------------------------------
function categoryUpdated() {
  if (categoryFilterValue.value !== selectedCategory.value) {
    selectedCategory.value = categoryFilterValue.value;

    // reset all filters
    offset.value = 0;
    ruleIndexFilterValue.value = [];
    selectedRuleIndexes.value = [];
    showCurrentOnly.value = false;
    showFavoritesOnly.value = false;
    showWithServicingRolesOnly.value = false;
    startDate.value = DateTime.local().minus({ days: 366 }).toJSDate();
    endDate.value = DateTime.local().minus({ days: 1 }).toJSDate();

    Promise.all([fetchListData(), fetchStats(), fetchViolationCount()]);
  }
}

function updateRuleSelection(event: Event, rule: { value: number; type?: string }) {
  const target = event.target as HTMLInputElement;
  const isChecked = target.checked;
  if (isChecked) {
    ruleIndexFilterValue.value.push(rule);
  } else {
    const index = ruleIndexFilterValue.value.findIndex((r) => r.value === rule.value && r.type === rule.type);
    if (index !== -1) {
      ruleIndexFilterValue.value.splice(index, 1);
    }
  }
}

function isRuleSelected(rule: { value: number; type?: string }) {
  return ruleIndexFilterValue.value.some((r) => r.value === rule.value && r.type === rule.type);
}

function ruleFilterUpdated() {
  if (ruleIndexFilterValue.value !== selectedRuleIndexes.value) {
    selectedRuleIndexes.value = ruleIndexFilterValue.value;
  }
  offset.value = 0;

  // refresh list data and count
  Promise.all([fetchListData(), fetchViolationCount()]);
}

function clearRuleFilter() {
  ruleIndexFilterValue.value = [];
}

function filterOptionsChanged() {
  offset.value = 0;

  // refresh list data and count
  Promise.all([fetchListData(), fetchViolationCount()]);
}

function previousPage() {
  const newOffset = offset.value - PAGE_SIZE;
  offset.value = newOffset < 0 ? 0 : newOffset;

  // refresh list data only
  fetchListData();
}

function nextPage() {
  offset.value += PAGE_SIZE;

  // refresh list data only
  fetchListData();
}

function setPage(page: number) {
  offset.value = (page - 1) * PAGE_SIZE;

  // refresh list data only
  fetchListData();
}

function setDate(datePayload: DatePayload) {
  if (datePayload.startDate) {
    startDate.value = datePayload.startDate;
  }
  if (datePayload.endDate) {
    endDate.value = datePayload.endDate;
  }

  // refresh list data and count
  Promise.all([fetchListData(), fetchViolationCount()]);
}

function saveEditPolicyModal() {
  showEditPolicyModal.value = false;
  editPolicyId.value = 0;

  Promise.all([fetchListData(), fetchStats(), fetchViolationCount()]);
}

function setSearchCriteriaFromQuery() {
  const query = route.query as DataQualityRouteQuery;
  if (Object.keys(query).length > 0) {
    selectedCategory.value = query.category || ACCOUNT_VIOLATIONS;
    if (selectedCategory.value !== ROLLUP_ENTITIES_VIOLATIONS) {
      showCurrentOnly.value = query.current === 'true';
      showFavoritesOnly.value = query.favorite === 'true';
      showWithServicingRolesOnly.value = query.roles === 'true';
    }

    if (selectedCategory.value === POLICY_VIOLATIONS) {
      if (query.start) startDate.value = DateTime.fromJSDate(new Date(query.start as string)).toJSDate();
      if (query.end) endDate.value = DateTime.fromJSDate(new Date(query.end as string)).toJSDate();
    }

    if (selectedCategory.value === ROLLUP_ENTITIES_VIOLATIONS) {
      const rollupEntityRules = ['policyTypeRules', 'issuingCompanyTypeRules', 'wholesalerTypeRules'];
      rollupEntityRules.forEach((ruleKey) => {
        const ruleValue = route.query[ruleKey];
        let rules: number[] = [];

        if (Array.isArray(ruleValue)) {
          rules = ruleValue.flatMap((rule) =>
            typeof rule === 'string' ? rule.split(',').map((id) => parseInt(id.trim())) : [],
          );
        } else if (typeof ruleValue === 'string') {
          rules = ruleValue.split(',').map((id) => parseInt(id.trim()));
        }

        if (rules.length) {
          selectedRuleIndexes.value = selectedRuleIndexes.value.concat(
            rules.map((value) => ({
              value,
              type: ruleKey.replace('Rules', ''),
            })),
          );
        }
      });
    } else {
      selectedRuleIndexes.value =
        query.rules?.split(',').reduce((rules, id) => {
          if (id && !isNaN(parseInt(id.trim()))) {
            rules.push({ value: parseInt(id.trim()) });
          }
          return rules;
        }, [] as RuleFilter[]) || [];
    }
  }
}

function searchCriteriaAsQuery() {
  const query: DataQualityRouteQuery = {};
  if (selectedCategory.value) query.category = selectedCategory.value;
  query.current = showCurrentOnly.value.toString();
  query.favorite = showFavoritesOnly.value.toString();
  query.roles = showWithServicingRolesOnly.value.toString();

  if (selectedRuleIndexes.value.length > 0) {
    if (selectedCategory.value === ROLLUP_ENTITIES_VIOLATIONS) {
      const policyTypeRules: string[] = [];
      const issuingCompanyTypeRules: string[] = [];
      const wholesalerTypeRules: string[] = [];

      selectedRuleIndexes.value.forEach(({ value, type }) => {
        if (type === 'Policy Type') {
          policyTypeRules.push(`Rule${value}`);
        } else if (type === 'Issuing Company') {
          issuingCompanyTypeRules.push(`Rule${value}`);
        } else if (type === 'Wholesaler') {
          wholesalerTypeRules.push(`Rule${value}`);
        }
      });

      if (policyTypeRules.length > 0) query.policyTypeRules = policyTypeRules.join(',');
      if (issuingCompanyTypeRules.length > 0) query.issuingCompanyTypeRules = issuingCompanyTypeRules.join(',');
      if (wholesalerTypeRules.length > 0) query.wholesalerTypeRules = wholesalerTypeRules.join(',');
    } else {
      query.rules = selectedRuleIndexes.value.map((i) => i.value).join();
    }
  }

  if (selectedCategory.value === POLICY_VIOLATIONS) {
    if (startDate.value) query.start = DateTime.fromJSDate(startDate.value).toLocaleString(DateTime.DATE_SHORT);
    if (endDate.value) query.end = DateTime.fromJSDate(endDate.value).toLocaleString(DateTime.DATE_SHORT);
  }

  return query;
}

async function loadData(fn: LoadFunction) {
  loadingAll.value = true;
  await fn();
  loadingAll.value = false;
}

async function loadViolationTable(fn: LoadFunction) {
  loadingViolations.value = true;
  await fn();
  loadingViolations.value = false;
}

async function loadPolicyViolations(): Promise<void> {
  return loadViolationTable(async () => {
    const violations = await dataQualityService.policyList({
      offset: offset.value,
      limit: PAGE_SIZE + 1,
      rules: effectiveRules.value.map((r) => r.value),
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
      startDate: DateTime.fromJSDate(startDate.value).toISODate(),
      endDate: DateTime.fromJSDate(endDate.value).toISODate(),
    });

    policyViolations.value = violations ?? [];
  });
}

async function loadPolicyStats(): Promise<void> {
  return loadData(async () => {
    const stats = await dataQualityService.policyListStats({
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
      startDate: DateTime.fromJSDate(startDate.value).toISODate(),
      endDate: DateTime.fromJSDate(endDate.value).toISODate(),
    });
    if (stats) {
      policyStats.value = stats;
      totalViolations.value = stats.totalViolations;
    }
  });
}

async function loadPolicyViolationCount(): Promise<void> {
  return loadData(async () => {
    const count = await dataQualityService.policyCount({
      rules: effectiveRules.value.map((r) => r.value),
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
      startDate: DateTime.fromJSDate(startDate.value).toISODate(),
      endDate: DateTime.fromJSDate(endDate.value).toISODate(),
    });

    filteredViolationCount.value = count ?? 0;
  });
}

async function loadAccountViolations(): Promise<void> {
  return loadViolationTable(async () => {
    const violations = await dataQualityService.accountList({
      offset: offset.value,
      limit: PAGE_SIZE + 1,
      rules: effectiveRules.value.map((r) => r.value),
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
    });

    accountViolations.value = violations ?? 0;
  });
}

async function loadAccountStats(): Promise<void> {
  return loadData(async () => {
    const stats = await dataQualityService.accountListStats({
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
    });
    if (stats) {
      accountStats.value = stats;
      totalViolations.value = stats.totalViolations;
    }
  });
}

async function loadAccountViolationCount(): Promise<void> {
  return loadData(async () => {
    const count = await dataQualityService.accountCount({
      rules: effectiveRules.value.map((r) => r.value),
      currentOnly: showCurrentOnly.value,
      favOnly: showFavoritesOnly.value,
      serviceRolesOnly: showWithServicingRolesOnly.value,
    });

    filteredViolationCount.value = count ?? 0;
  });
}

async function loadRollupEntityViolations(): Promise<void> {
  return loadViolationTable(async () => {
    const rollupEntityRules: RollupEntityRules = {
      policyTypeRules: [],
      issuingCompanyTypeRules: [],
      wholesalerTypeRules: [],
    };

    effectiveRules.value.forEach((rule) => {
      if (rule.type === 'Policy Type') {
        rollupEntityRules.policyTypeRules.push(rule.value);
      } else if (rule.type === 'Issuing Company') {
        rollupEntityRules.issuingCompanyTypeRules.push(rule.value);
      } else if (rule.type === 'Wholesaler') {
        rollupEntityRules.wholesalerTypeRules.push(rule.value);
      }
    });

    const violations = await dataQualityService.rollupEntityList({
      offset: offset.value,
      limit: PAGE_SIZE + 1,
      rules: rollupEntityRules,
    });

    rollupEntityViolations.value = violations ?? [];
  });
}

async function loadRollupEntityStats(): Promise<void> {
  return loadData(async () => {
    const stats = await dataQualityService.rollupEntityListStats();
    if (stats) {
      rollupEntityStats.value = stats;
      totalViolations.value = stats.totalEntitiesWithViolations;
    }
  });
}

async function loadRollupEntityViolationCount(): Promise<void> {
  return loadData(async () => {
    const rollupEntityRules: RollupEntityRules = {
      policyTypeRules: [],
      issuingCompanyTypeRules: [],
      wholesalerTypeRules: [],
    };

    effectiveRules.value.forEach((rule) => {
      if (rule.type === 'Policy Type') {
        rollupEntityRules.policyTypeRules.push(rule.value);
      } else if (rule.type === 'Issuing Company') {
        rollupEntityRules.issuingCompanyTypeRules.push(rule.value);
      } else if (rule.type === 'Wholesaler') {
        rollupEntityRules.wholesalerTypeRules.push(rule.value);
      }
    });

    const count = await dataQualityService.rollupEntityCount(rollupEntityRules);
    filteredViolationCount.value = count ?? 0;
  });
}

async function loadProgramViolations(): Promise<void> {
  return loadViolationTable(async () => {
    programViolations.value = [];
    const violations = await dataQualityService.programList({
      offset: offset.value,
      limit: PAGE_SIZE + 1,
      rules: effectiveRules.value.map((r) => r.value),
    });

    programViolations.value = violations ?? [];
  });
}

async function loadProgramStats(): Promise<void> {
  return loadData(async () => {
    const stats = await dataQualityService.programListStats();

    if (stats) {
      programStats.value = stats;
      totalViolations.value = stats.totalViolations;
    }
  });
}

async function loadProgramViolationCount(): Promise<void> {
  return loadData(async () => {
    const count = await dataQualityService.programCount(effectiveRules.value.map((r) => r.value));
    filteredViolationCount.value = count ?? 0;
  });
}

async function fetchListData(): Promise<void> {
  // pull query data from route
  const query = searchCriteriaAsQuery();

  if (JSON.stringify(route.query) !== JSON.stringify(query)) {
    await router.replace({ query }).catch(() => {
      return '';
    });
  }

  const loadMap = new Map([
    [PROGRAM_VIOLATIONS, loadProgramViolations],
    [ROLLUP_ENTITIES_VIOLATIONS, loadRollupEntityViolations],
    [POLICY_VIOLATIONS, loadPolicyViolations],
    [ACCOUNT_VIOLATIONS, loadAccountViolations],
  ]);
  const loader = loadMap.get(selectedCategory.value);
  if (loader) {
    return loader();
  }
}

async function fetchStats(): Promise<void> {
  const loadMap = new Map([
    [PROGRAM_VIOLATIONS, loadProgramStats],
    [ROLLUP_ENTITIES_VIOLATIONS, loadRollupEntityStats],
    [POLICY_VIOLATIONS, loadPolicyStats],
    [ACCOUNT_VIOLATIONS, loadAccountStats],
  ]);
  const loader = loadMap.get(selectedCategory.value);
  if (loader) {
    return loader();
  }
}

async function fetchViolationCount(): Promise<any> {
  // pull query data from route
  const query = searchCriteriaAsQuery();

  if (JSON.stringify(route.query) !== JSON.stringify(query)) {
    await router.replace({ query }).catch(() => {
      return '';
    });
  }

  const loadMap = new Map([
    [PROGRAM_VIOLATIONS, loadProgramViolationCount],
    [ROLLUP_ENTITIES_VIOLATIONS, loadRollupEntityViolationCount],
    [POLICY_VIOLATIONS, loadPolicyViolationCount],
    [ACCOUNT_VIOLATIONS, loadAccountViolationCount],
  ]);
  const loader = loadMap.get(selectedCategory.value);
  if (loader) {
    return loader();
  }
}
</script>

<template>
  <div>
    <div class="page-header inner tw:mt-4">
      <h1>Data Quality</h1>
      <ManagedDateRange
        v-if="showingPolicyViolations"
        :initialStartDate="startDate"
        :initialEndDate="endDate"
        :onApply="setDate"
        data-test="date-range-filter" />
    </div>
    <FilterBar>
      <div class="filter-menu">
        <div style="display: flex">
          <FilterButton
            :title="violationCategoryTitle"
            @close="categoryUpdated"
            :active="true"
            data-testid="data-quality-category-filter">
            <template #menu="{ close }">
              <div class="menu">
                <div class="fields">
                  <h4>Category</h4>
                  <div class="wrapper">
                    <div class="field">
                      <label
                        v-for="category in categories"
                        :key="category"
                        :data-test="`category-${category.replaceAll(' ', '-')}`">
                        <input type="radio" :value="category" v-model="categoryFilterValue" />
                        <span>{{ category }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div class="buttons">
                  <div>&nbsp;</div>
                  <v-btn
                    size="small"
                    variant="flat"
                    color="primary"
                    @click="close"
                    data-testid="data-quality-view-options-apply"
                    >Done
                  </v-btn>
                </div>
              </div>
            </template>
          </FilterButton>
          <FilterButton
            :title="ruleFilterTitle"
            @close="ruleFilterUpdated"
            :active="ruleFilterActive"
            data-test="data-quality-rules-filter">
            <template #menu="{ close }">
              <div class="menu menu-rules" :class="showingAccountViolations ? 'account' : 'policy'">
                <div class="fields">
                  <h4>Rules</h4>
                  <div v-if="!showingRollupEntitiesViolations" class="wrapper">
                    <div class="field" v-for="rule in availableRules" :key="rule.key" :data-test="`rule-${rule.key}`">
                      <label>
                        <input
                          type="checkbox"
                          :value="{ value: rule.value, type: rule.type }"
                          @change="updateRuleSelection($event, { value: rule.value, type: rule.type })"
                          :checked="isRuleSelected(rule)" />
                        <span>{{ rule.title }}</span>
                        <p>{{ rule.desc }}</p>
                      </label>
                    </div>
                  </div>
                  <div v-else class="tw:grid-col-1 tw:ml-3 tw:min-w-[500px] tw:grid-flow-col tw:grid-rows-3">
                    <div v-for="(rules, type) in groupedRules" :key="type" class="tw:p-1">
                      <h4 class="tw:ml-3">{{ type }}</h4>
                      <div class="tw:ml-6" v-for="rule in rules" :key="rule.key" :data-test="`rule-${rule.key}`">
                        <label>
                          <input
                            type="checkbox"
                            :value="{ value: rule.value, type: rule.type }"
                            @change="updateRuleSelection($event, { value: rule.value, type: rule.type })"
                            :checked="isRuleSelected(rule)" />
                          <span class="tw:ml-2 tw:font-medium">{{ rule.title }}</span>
                          <p class="tw:ml-5 tw:text-[0.8rem]">{{ rule.desc }}</p>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="buttons">
                  <v-btn size="small" variant="outlined" @click="clearRuleFilter" data-test="data-quality-rules-clear">
                    Clear
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="flat"
                    color="primary"
                    @click="close"
data-testid="data-quality-view-options-apply"
                  >Done
                  </v-btn>
                </div>
              </div>
            </template>
          </FilterButton>
        </div>
        <div v-if="showToggleFilters" class="tw:flex tw:gap-4">
          <toggle
            id="show-current-only"
            class="tw:text-xs"
            label="Current Only"
            v-model="showCurrentOnly"
            @change="filterOptionsChanged" />
          <toggle
            id="favorites"
            class="tw:text-xs"
            label="Favorites Only"
            v-model="showFavoritesOnly"
            @change="filterOptionsChanged" />
          <toggle
            id="has-servicing-roles"
            class="tw:text-xs"
            label="Servicing Roles"
            v-model="showWithServicingRolesOnly"
            @change="filterOptionsChanged" />
        </div>
      </div>
    </FilterBar>

    <div class="inner">
      <v-skeleton-loader type="table-tbody" v-if="loadingAll" />

      <div class="tw:mb-8 tw:flex tw:flex-col" v-else>
        <div
          v-if="showingAccountViolations"
          class="tw:mx-auto tw:flex tw:w-full tw:max-w-[1500px] tw:min-w-[800px] tw:flex-col">
          <main class="tw:mt-1 tw:grid tw:grid-cols-1 tw:grid-rows-2 tw:gap-4 tw:lg:grid-cols-2 tw:lg:grid-rows-1">
            <div
              class="tw:grid-start-1 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                class="tw:grid-start-1"
                :dataSource="accountDataQuality"></fusioncharts>
            </div>

            <div
              class="tw:grid-start-1 tw:lg:grid-start-2 tw:row-start-2 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md tw:lg:row-start-1">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                class="tw:grid-start-2"
                :dataSource="accountViolationsByRule"></fusioncharts>
            </div>
          </main>

          <EmptyDisplay
            v-if="!loadingViolations && noViolations"
            :src="filingSrc"
            bordered
            title="No violations"
            text="No violations were found for the selected rules." />

          <div v-else class="tw:mt-10 tw:flex tw:w-full tw:flex-col tw:self-center">
            <h2 class="tw:sticky tw:top-[64px] tw:z-50 tw:bg-base-100 tw:py-4">Account Violations</h2>

            <div class="tw:flex tw:justify-center">
              <table
                id="data-quality-account-table"
                class="apptable dense --tight-first mt-4 tw:max-w-[2000px]"
                v-if="!loadingViolations">
                <thead class="tw:sticky tw:top-[122px] tw:z-50">
                  <DataQualityAccountRow header />
                </thead>
                <tbody>
                  <DataQualityAccountRow
                    v-for="account in accountViolations"
                    :key="account.accountID"
                    :account="account"
                    :rules="accountRules" />
                </tbody>
              </table>
              <v-skeleton-loader v-else type="table-tbody" height="100%" />
            </div>
          </div>
        </div>

        <div v-if="showingPolicyViolations" class="tw:mx-auto tw:flex tw:w-full tw:max-w-[1800px] tw:flex-col">
          <main class="tw:mt-1 tw:grid tw:grid-cols-1 tw:grid-rows-2 tw:gap-4 tw:lg:grid-cols-2 tw:lg:grid-rows-1">
            <div
              class="tw:grid-start-1 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                :dataSource="policyDataQuality"></fusioncharts>
            </div>

            <div
              class="tw:grid-start-1 tw:lg:grid-start-2 tw:row-start-2 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md tw:lg:row-start-1">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                :dataSource="policyViolationsByRule"></fusioncharts>
            </div>
          </main>
          <EmptyDisplay
            :src="filingSrc"
            bordered
            title="No violations"
            text="No violations were found for the selected rules."
            v-if="!loadingViolations && noViolations" />
          <div v-else class="tw:mt-10 tw:flex tw:w-full tw:flex-col tw:self-center">
            <h2 class="tw:sticky tw:top-[64px] tw:z-50 tw:bg-base-100 tw:py-4">Policy Violations</h2>

            <div class="tw:flex tw:justify-center">
              <table id="data-quality-policy-table" class="apptable dense --tight-first" v-if="!loadingViolations">
                <thead class="tw:sticky tw:top-[122px] tw:z-50">
                  <DataQualityPolicyRow header />
                </thead>
                <tbody>
                  <DataQualityPolicyRow
                    v-for="(violation, index) in policyViolations"
                    :key="`${violation.policyID}-${index}`"
                    :violation="violation"
                    :rules="policyRules"
                    @edit-policy="
                      (policyId, policyNumber) => {
                        showEditPolicyModal = true;
                        editPolicyId = policyId;
                        editPolicyNumber = policyNumber;
                      }
                    " />
                </tbody>
              </table>
              <v-skeleton-loader v-else type="table-tbody" class="tw:h-full" />
            </div>
          </div>
        </div>

        <div
          v-if="showingRollupEntitiesViolations"
          class="tw:mx-auto tw:flex tw:w-full tw:max-w-[1500px] tw:min-w-[600px] tw:flex-col">
          <main class="tw:mt-1 tw:grid tw:grid-cols-1 tw:grid-rows-2 tw:gap-4 tw:lg:grid-cols-2 tw:lg:grid-rows-1">
            <div
              class="tw:grid-start-1 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                :dataSource="rollupEntityDataQuality"></fusioncharts>
            </div>

            <div
              class="tw:grid-start-1 tw:lg:grid-start-2 tw:row-start-2 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md tw:lg:row-start-1">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                class="tw:grid-start-2"
                :dataSource="rollupEntityViolationsByRule"></fusioncharts>
            </div>
          </main>

          <EmptyDisplay
            v-if="!loadingViolations && noViolations"
            :src="filingSrc"
            bordered
            title="No violations"
            text="No violations were found for the selected rules." />

          <div v-else class="tw:mt-10 tw:flex tw:w-full tw:flex-col tw:self-center">
            <h2 class="tw:sticky tw:top-[64px] tw:z-50 tw:bg-base-100 tw:py-4">Rollup Entity Violations</h2>
            <div class="tw:flex tw:justify-center">
              <table v-if="!loadingViolations" class="apptable dense --tight-first tw:mt-4">
                <thead class="tw:sticky tw:top-[122px] tw:z-50">
                  <DataQualityRollupEntityRow header />
                </thead>
                <tbody>
                  <DataQualityRollupEntityRow
                    v-for="(violation, index) in rollupEntityViolations"
                    :key="`${violation.entityType}-${index}`"
                    :violation="violation"
                    :policyTypeRules="rollupEntityPolicyTypeRules"
                    :issuingCompanyRules="rollupEntityIssuingCompanyRules"
                    :wholesalerRules="rollupEntityWholesalerRules" />
                </tbody>
              </table>
              <v-skeleton-loader v-else type="table-tbody" class="tw:h-full" />
            </div>
          </div>
        </div>

        <div
          v-if="showingProgramViolations"
          class="tw:mx-auto tw:flex tw:w-full tw:max-w-[1500px] tw:min-w-[600px] tw:flex-col">
          <main class="tw:mt-1 tw:grid tw:grid-cols-1 tw:grid-rows-2 tw:gap-4 tw:lg:grid-cols-2 tw:lg:grid-rows-1">
            <div
              class="tw:grid-start-1 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                class="tw:grid-start-1"
                :dataSource="programDataQuality"></fusioncharts>
            </div>

            <div
              class="tw:grid-start-1 tw:lg:grid-start-2 tw:row-start-2 tw:overflow-hidden tw:rounded-2xl tw:border tw:border-solid tw:border-gray-100 tw:shadow-md tw:lg:row-start-1">
              <fusioncharts
                type="pie2d"
                width="100%"
                height="400"
                dataFormat="json"
                class="tw:grid-start-2"
                :dataSource="programViolationsByRule"></fusioncharts>
            </div>
          </main>

          <EmptyDisplay
            :src="filingSrc"
            bordered
            title="No violations"
            text="No violations were found for the selected rules."
            v-if="!loadingViolations && noViolations" />

          <div v-else class="tw:mt-10 tw:flex tw:w-full tw:flex-col tw:self-center">
            <h2 class="tw:sticky tw:top-[64px] tw:z-50 tw:bg-base-100 tw:py-4">Program Violations</h2>

            <table
              id="data-quality-policy-table"
              class="apptable dense --tight-first tw:mt-4"
              v-if="!loadingViolations">
              <thead class="tw:sticky tw:top-[122px] tw:z-50">
                <DataQualityProgramRow header />
              </thead>
              <tbody>
                <DataQualityProgramRow
                  v-for="(violation, index) in programViolations"
                  :key="`${violation.programId}-${index}`"
                  :violation="violation"
                  :rules="programRules" />
              </tbody>
            </table>
            <v-skeleton-loader v-else type="table-tbody" class="tw:h-full" />
          </div>
        </div>

        <div class="pagination" v-if="!loadingViolations">
          <paging-control
            :current-page="currPage"
            :total-pages="totalPages"
            @next-page="nextPage"
            @previous-page="previousPage"
            @set-page="setPage">
          </paging-control>
        </div>
      </div>
    </div>

    <EditPolicyModal
      :show="showEditPolicyModal"
      :policy-id="editPolicyId"
      :policy-number="editPolicyNumber"
      @close="showEditPolicyModal = false"
      @save="saveEditPolicyModal" />
  </div>
</template>

<style scoped>
.menu-rules.account > .fields > .wrapper {
  grid-auto-flow: column;
  grid-template-rows: repeat(5, 1fr);
}

.menu-rules > .fields > .wrapper > .field > label > p {
  grid-column: 2 / 4;
  margin: 0 0 0.2rem 0;
  padding: 0;
  font-size: 0.8rem;
}

.menu-rules.policy > .fields > .wrapper {
  grid-auto-flow: column;
  grid-template-rows: repeat(6, 1fr);
}

.menu-rules.policy > .fields > .wrapper > .field > label > p {
  max-width: 27rem;
}

.menu-rules > .fields > .wrapper > .field > label > span {
  font-weight: 700;
}

.filter-menu {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.page-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding-top: 0;
}
</style>
